import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";

const StyledLinearProgress = styled(LinearProgress)({
  cursor: "help",
  position: "relative",
  flexGrow: 1,
  flexShrink: 1,
  marginRight: 1,
  color: "black",
  height: 10,
});

const Root = styled("div")({
  display: "flex",
});

interface ProgressCellProps {
  steps?: (string | null)[] | null;
  progress?: number | null;
  state?: string | null;
  color?: "primary" | "secondary" | "info" | "success" | "warning" | "error";
  style?: React.CSSProperties;
}

/**
 * Прогресс задачи. У терминальных статусов полоска одна и целиком задаёт
 * состояние, у идущей задачи — по полоске на шаг: воркер отдаёт общий процент,
 * а шаги режут его на равные отрезки.
 */
export function ProgressCell({
  steps,
  progress,
  state,
  color,
  style,
}: ProgressCellProps) {
  switch (state) {
    case "waiting":
      return (
        <Tooltip title="Ожидает">
          <StyledLinearProgress color="info" variant="determinate" value={0} />
        </Tooltip>
      );

    case "failed":
      return (
        <Tooltip title="Ошибка">
          <StyledLinearProgress color="error" variant="determinate" value={0} />
        </Tooltip>
      );

    case "completed":
      return (
        <Tooltip title="Завершена">
          <StyledLinearProgress
            color="success"
            variant="determinate"
            value={100}
          />
        </Tooltip>
      );

    default: {
      const jobSteps = (steps || []).filter(
        (step): step is string => typeof step === "string"
      );
      const value = progress || 0;

      if (jobSteps.length === 0) {
        return (
          <Root style={style}>
            <StyledLinearProgress
              variant="determinate"
              value={value}
              color={color}
            />
          </Root>
        );
      }

      // Сколько процентов общего прогресса занимает один шаг
      const oneStepLength = 100 / jobSteps.length;
      // В каком шаге сейчас находится прогресс
      const currentStepIndex = Math.floor(value / oneStepLength);
      const currentStepProgress = (value % oneStepLength) * jobSteps.length;

      return (
        <Root style={style}>
          {jobSteps.map((step, index) => {
            let stepProgress = 0;

            if (currentStepIndex === index) {
              stepProgress = Math.floor(currentStepProgress);
            } else if (index < currentStepIndex) {
              stepProgress = 100;
            }

            return (
              <Tooltip key={index} title={`${stepProgress}% ${step}`}>
                <StyledLinearProgress
                  variant="determinate"
                  value={stepProgress}
                  color={color}
                />
              </Tooltip>
            );
          })}
        </Root>
      );
    }
  }
}
